import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { ThemeProvider, createTheme, withStyles } from "@material-ui/core/styles";
import Divider from "@material-ui/core/Divider";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Collapse from "@material-ui/core/Collapse";
import ListItem from "@material-ui/core/ListItem";
import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender } from "@tanstack/react-table";
import MomentUtils from "@date-io/moment";
import { DateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import NumericalTextField from "../../../material-components/Misc/NumericalTextField";
import { DeliveryType } from "../../../../enums/company";
import {
    updateAttribute,
    getUpdatedCPMandPOM,
    syncScheduleEndDateWithExpiration,
} from "../../../../actions/flowActions";
import { requestCompanies, setLoadingCompany } from "../../../../actions/userActions";
import { formatExpirationDate, parseDateToTimeZoneWithTime } from "../../../../helpers/parseDate";

// used to remove arrow icons from number text fields
// cant have those because we fail to update on click from 1 -> 0
// due to using both selects and text fields
const styles = {
    input: {
        "&[type=number]": {
            "-moz-appearance": "textfield",
        },
        "&::-webkit-outer-spin-button": {
            "-webkit-appearance": "none",
            margin: 0,
        },
        "&::-webkit-inner-spin-button": {
            "-webkit-appearance": "none",
            margin: 0,
        },
    },
};

const ExpandLess = <i className="material-icons"> expand_less </i>;
const ExpandMore = <i className="material-icons"> expand_more </i>;
const USDollar = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
});

const ExportPubmaticTemplateFields = ({
    tapadDeliveryTypeId,
    destinationId,
    flowExportPubmaticTemplateFields,
    canEdit,
    classes,
}) => {
    const dispatch = useDispatch();

    const defaultExpansionType = "HOUSEHOLD";

    // state
    const [templateRows, setTemplateRows] = useState([]);
    const [deliveryType, setDeliveryType] = useState();
    const [isOpenGlobalAssignment, setIsOpenGlobalAssignment] = useState(false);
    const [globalExpansionType, setGlobalExpansionType] = useState(defaultExpansionType);
    const [globalPercentOfMediaCost, setGlobalPercentOfMediaCost] = useState(0);
    const [globalCostPerMillion, setGlobalCostPerMillion] = useState(0);
    const [globalExpirationDate, setGlobalExpirationDate] = useState();
    const [refreshTable, setRefreshTable] = useState(0);
    const [sorting, setSorting] = useState([]);

    // props
    const companies = useSelector(state => state.companies.byId);
    const loadingCompanies = useSelector(state => state.vars.loadingCompanies);
    const flow = useSelector(state => state.flows.byId[state.selected.flow]);
    const timeZoneIana = useSelector(state => state.session.userSettings["time-zone-iana"]);
    const tapadDeliveryTypes = useSelector(state => state.vars.tapadDeliveryTypes);
    const enabledFeatures = useSelector(state => state.session.enabledFeatures);

    // Variables use to calcuate CPM and POM
    const flowItems = useSelector(state => state.flowItems.byId);
    const hasFeatureDisplayFieldRates = enabledFeatures.includes("display-field-rates");
    const hasScheduleTemplatesFeature = enabledFeatures.includes("schedule-templates");

    const renderTemplateRows = () => {
        let rows = [];

        // Set default parentElementId and ParentElementName
        let defaultParentElementId = "";
        let defaultParentElementName = "";

        if (deliveryType) {
            if (DeliveryType.ThirdPartyCustom == deliveryType.DeliveryType) {
                defaultParentElementId = "";
                defaultParentElementName = "";
            }
        }

        if (flowExportPubmaticTemplateFields != null) {
            for (const template of flowExportPubmaticTemplateFields) {
                // if the date is null, not showing a default date because it makes the user think it's good
                // only happens when saving an old flow with null ExpirationDate
                const formattedRunDate = template.ExpirationDate
                    ? hasScheduleTemplatesFeature
                        ? formatExpirationDate(template.ExpirationDate)
                        : new Date(parseDateToTimeZoneWithTime(template.ExpirationDate, timeZoneIana))
                    : null;

                if (deliveryType && DeliveryType.ThirdPartyCustom == deliveryType.DeliveryType) {
                    if (template.ParentElementId == null) {
                        dispatch(
                            updateAttribute(
                                "flowExportPubmaticTemplateFields",
                                template.FlowExportTemplateId,
                                "ParentElementId",
                                defaultParentElementId
                            )
                        );
                    }

                    if (template.ParentElementName == null) {
                        dispatch(
                            updateAttribute(
                                "flowExportPubmaticTemplateFields",
                                template.FlowExportTemplateId,
                                "ParentElementName",
                                defaultParentElementName
                            )
                        );
                    }
                }

                // Default Expansion Type
                if (!template.ExpansionType || template.ExpansionType == "") {
                    dispatch(
                        updateAttribute(
                            "flowExportPubmaticTemplateFields",
                            template.FlowExportTemplateId,
                            "ExpansionType",
                            defaultExpansionType
                        )
                    );
                }

                if (deliveryType && DeliveryType.ThirdPartyCustom == deliveryType.DeliveryType) {
                    if (template.RateType != "Hybrid" && template.RateType != "") {
                        handleRateTypeChange(template.FlowExportTemplateId, "");
                    }
                }

                rows.push({
                    FlowExportTemplateId: template.FlowExportTemplateId,
                    ParentFlowItemId: template.ParentFlowItemId,
                    AudienceName: template.AudienceName || "",
                    Description: template.Description || "",
                    ExpansionType: template.ExpansionType || defaultExpansionType,
                    RateType: "",
                    CostPerMillion: template.CostPerMillion || 0,
                    PercentOfMediaCost: template.PercentOfMediaCost || 0,
                    TTL: template.TTL || 0,
                    ExpirationDate: formattedRunDate,
                    ProviderElementId: template.ProviderElementId || "",
                    ParentElementId: template.ParentElementId != null ? "" : defaultParentElementId,
                    ParentElementName: template.ParentElementName != null ? "" : defaultParentElementName,
                    IsAutoGeneratedCPM: template.IsAutoGeneratedCPM,
                    IsAutoGeneratedPOM: template.IsAutoGeneratedPOM,
                });
            }
        }

        setTemplateRows(rows);
        setRefreshTable(refreshTable + 1);
    };

    // effects
    useEffect(() => {
        renderTemplateRows();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        destinationId,
        timeZoneIana,
        deliveryType,
        dispatch,
        flowExportPubmaticTemplateFields,
        hasScheduleTemplatesFeature,
    ]);

    // effects

    useEffect(() => {
        if (!companies || (Object.keys(companies).length <= 0 && !loadingCompanies)) {
            dispatch(setLoadingCompany(true));
            dispatch(requestCompanies());
        }
    }, [dispatch, companies, loadingCompanies]);

    useEffect(() => {
        const thisDeliveryType = tapadDeliveryTypes.find(x => x.TapadDeliveryTypeId == tapadDeliveryTypeId);

        if (deliveryType && thisDeliveryType?.TapadDeliveryTypeId != deliveryType.TapadDeliveryTypeId) {
            // addMissingFlowOfferMerges
            for (let i = 0; i < flowExportPubmaticTemplateFields.length; i++) {
                const flowItemsArray = Object.values(flowItems);
                const FlowItem = flowItemsArray.find(
                    x => x.FlowItemId == flowExportPubmaticTemplateFields[i].ParentFlowItemId
                );

                const newCPMValue = dispatch(
                    getUpdatedCPMandPOM(
                        FlowItem,
                        flowExportPubmaticTemplateFields[i].FlowItemId,
                        flowExportPubmaticTemplateFields[i].ParentFlowItemId,
                        hasFeatureDisplayFieldRates,
                        1,
                        false
                    )
                );
                flowExportPubmaticTemplateFields[i].CostPerMillion = newCPMValue;
                flowExportPubmaticTemplateFields[i].PercentOfMediaCost = 0;
                flowExportPubmaticTemplateFields[i].IsAutoGeneratedCPM = true;
                flowExportPubmaticTemplateFields[i].IsAutoGeneratedPOM = true;
            }
            setTemplateRows(flowExportPubmaticTemplateFields);
            renderTemplateRows();
            setRefreshTable(refreshTable + 1);
        }
        if (thisDeliveryType && flowExportPubmaticTemplateFields.length > 0) {
            setDeliveryType(thisDeliveryType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tapadDeliveryTypeId, flowExportPubmaticTemplateFields]);

    const selectAudienceName = cellInfo => {
        const rows = [...templateRows];
        const column = (
            <TextField
                key={cellInfo.tableData.id + "-audience-name-" + cellInfo.FlowExportTemplateId}
                type="text"
                defaultValue={cellInfo.AudienceName}
                disabled={!canEdit || (flow && flow.IsLocked)}
                inputProps={{
                    style: { fontSize: "16px" },
                    maxLength: 100,
                }}
                onChange={e => {
                    rows[cellInfo.tableData.id].AudienceName = e.target.value;
                    setTemplateRows(rows);
                }}
                onBlur={e => handleChange(cellInfo.FlowExportTemplateId, "AudienceName", e)}
                fullWidth
            />
        );
        return column;
    };

    const selectDescription = cellInfo => {
        const rows = [...templateRows];
        const column = (
            <TextField
                key={cellInfo.tableData.id + "-template-description-" + cellInfo.FlowExportTemplateId}
                type="text"
                defaultValue={cellInfo.Description}
                disabled={!canEdit || (flow && flow.IsLocked)}
                inputProps={{
                    style: { fontSize: "16px" },
                }}
                onChange={e => {
                    rows[cellInfo.tableData.id].Description = e.target.value;
                    setTemplateRows(rows);
                }}
                onBlur={e => handleChange(cellInfo.FlowExportTemplateId, "Description", e)}
                fullWidth
            />
        );
        return column;
    };

    const selectExpansionType = cellInfo => {
        const rows = [...templateRows];
        const column = (
            <Select
                key={cellInfo.tableData.id + "expansion-type" + cellInfo.FlowExportTemplateId}
                style={{ fontSize: "14px", width: "100%" }}
                value={cellInfo.ExpansionType}
                onChange={e => {
                    rows[cellInfo.tableData.id].ExpansionType = e.target.value;
                    setTemplateRows(rows);
                    handleExpansionTypeChange(cellInfo.FlowExportTemplateId, e);
                }}
                disabled={!canEdit || (flow && flow.IsLocked)}
                displayEmpty
            >
                <MenuItem key={2} value={"HOUSEHOLD"}>
                    Household
                </MenuItem>
            </Select>
        );
        return column;
    };

    const selectExpirationDate = cellInfo => {
        const rows = [...templateRows];
        const column = (
            <MuiPickersUtilsProvider utils={MomentUtils}>
                <DateTimePicker
                    key={cellInfo.tableData.id + "-expiration-date-" + cellInfo.FlowExportTemplateId}
                    format={"MMM DD, YYYY HH:mm"}
                    value={cellInfo.ExpirationDate}
                    disabled={!canEdit || (flow && flow.IsLocked)}
                    onChange={e => {
                        const newDateTime = hasScheduleTemplatesFeature ? formatExpirationDate(e) : e?.utc().format();
                        rows[cellInfo.tableData.id].ExpirationDate = newDateTime;
                        setTemplateRows(rows);
                        handleDateTimeChange(cellInfo.FlowExportTemplateId, "ExpirationDate", newDateTime);
                        setRefreshTable(refreshTable + 1);
                    }}
                />
            </MuiPickersUtilsProvider>
        );
        return column;
    };

    const getCellStyleCPM = (cellInfo, rowData) => {
        // const rows = [...templateRows];
        // var myRow = rows[cellInfo.FlowExportTemplateId];
        let isAutoGenerated = rowData.IsAutoGeneratedCPM;
        let cellstyleJSON;
        if (isAutoGenerated && hasFeatureDisplayFieldRates) {
            cellstyleJSON = {
                backgroundColor: "#87CEEB",
                color: "#OOOO",
            };
        } else {
            cellstyleJSON = {
                backgroundColor: "#FFFF",
                color: "#OOOO",
            };
        }

        return cellstyleJSON;
    };
    const selectCostPerMillion = cellInfo => {
        const rows = [...templateRows];
        const column = (
            <NumericalTextField
                key={cellInfo.tableData.id + "-CostPerMillion-" + cellInfo.FlowExportTemplateId}
                classes={classes}
                value={USDollar.format(cellInfo.CostPerMillion)}
                disabled={!canEdit || (flow && flow.IsLocked)}
                numberType="currency"
                onChange={e => {
                    rows[cellInfo.tableData.id].CostPerMillion = e.target.value.replace(/[^0-9.]/g, "");
                    rows[cellInfo.tableData.id].IsAutoGeneratedCPM = false;
                    setTemplateRows(rows);
                }}
                onBlur={e => {
                    handlePositiveValueChange(
                        cellInfo.tableData.id,
                        cellInfo.FlowExportTemplateId,
                        "CostPerMillion",
                        e
                    );
                }}
            />
        );
        return column;
    };

    const handleChange = (TemplateId, attributeName, e) =>
        dispatch(updateAttribute("flowExportPubmaticTemplateFields", TemplateId, attributeName, e.target.value));

    const handleDateTimeChange = (TemplateId, attributeName, newDateTime) => {
        dispatch(updateAttribute("flowExportPubmaticTemplateFields", TemplateId, attributeName, newDateTime));
        if (attributeName == "ExpirationDate") {
            dispatch(syncScheduleEndDateWithExpiration(newDateTime));
        }
    };

    const handleGlobalDateTimeChange = newDateTime => {
        const utcDateTime = hasScheduleTemplatesFeature
            ? formatExpirationDate(newDateTime)
            : newDateTime?.utc().format();
        setGlobalExpirationDate(utcDateTime);
    };

    const handlePositiveValueChange = (rowID, TemplateId, attributeName, e) => {
        const rows = [...templateRows];
        const templateIneed = flowExportPubmaticTemplateFields.find(x => x.FlowExportTemplateId == TemplateId);
        let positiveValue = 0;
        if (e.target.value != null) {
            let thisVal = e.target.value.replace(/[^0-9.]/g, "");

            if (thisVal > 0) {
                positiveValue = thisVal;
            }
        }
        if (attributeName == "CostPerMillion" && templateIneed.CostPerMillion == positiveValue) {
            return;
        }
        if (attributeName == "PercentOfMediaCost" && templateIneed.PercentOfMediaCost == positiveValue) {
            return;
        }
        //tapadDeliveryTypes.find(x => x.TapadDeliveryTypeId == tapadDeliveryTypeId);
        if (attributeName == "CostPerMillion") {
            dispatch(
                updateAttribute("flowExportPubmaticTemplateFields", TemplateId, "IsAutoGeneratedCPM", false, true)
            );
            rows[rowID].IsAutoGeneratedCPM = false;
        } else {
            dispatch(
                updateAttribute("flowExportPubmaticTemplateFields", TemplateId, "IsAutoGeneratedPOM", false, true)
            );
            rows[rowID].IsAutoGeneratedPOM = false;
        }
        dispatch(updateAttribute("flowExportPubmaticTemplateFields", TemplateId, attributeName, positiveValue, true));
        setTemplateRows(rows);
        //  setRefreshTable(refreshTable + 1);
    };

    const handleGlobalPositiveValueChange = (attributeName, e) => {
        let positiveValue = 0;
        if (e.target.value != null) {
            let thisVal = e.target.value.replace(/[^0-9.]/g, "");

            if (thisVal > 0) {
                positiveValue = thisVal;
            }
        }

        if (attributeName == "PercentOfMediaCost") {
            setGlobalPercentOfMediaCost(0);
        } else {
            setGlobalCostPerMillion(positiveValue);
        }
    };

    const handleRateTypeChange = (TemplateId, value) =>
        dispatch(updateAttribute("flowExportPubmaticTemplateFields", TemplateId, "RateType", value));

    const handleExpansionTypeChange = (TemplateId, e) =>
        dispatch(updateAttribute("flowExportPubmaticTemplateFields", TemplateId, "ExpansionType", e.target.value));

    const assignGlobalValues = () => {
        if (flowExportPubmaticTemplateFields != null) {
            for (const template of flowExportPubmaticTemplateFields) {
                // Set Expansion Type
                dispatch(
                    updateAttribute(
                        "flowExportPubmaticTemplateFields",
                        template.FlowExportTemplateId,
                        "ExpansionType",
                        globalExpansionType
                    )
                );

                // Set Expiration Date
                dispatch(
                    updateAttribute(
                        "flowExportPubmaticTemplateFields",
                        template.FlowExportTemplateId,
                        "ExpirationDate",
                        globalExpirationDate
                    )
                );

                if (DeliveryType.ThirdPartyCustom == deliveryType?.DeliveryType) {
                    // Set Percent Of Media Cost
                    dispatch(
                        updateAttribute(
                            "flowExportPubmaticTemplateFields",
                            template.FlowExportTemplateId,
                            "PercentOfMediaCost",
                            globalPercentOfMediaCost
                        )
                    );

                    // Set Cost Per Million
                    dispatch(
                        updateAttribute(
                            "flowExportPubmaticTemplateFields",
                            template.FlowExportTemplateId,
                            "CostPerMillion",
                            globalCostPerMillion
                        )
                    );
                    // Set IsAutoGenerated
                    dispatch(
                        updateAttribute(
                            "flowExportPubmaticTemplateFields",
                            template.FlowExportTemplateId,
                            "IsAutoGeneratedCPM",
                            false
                        )
                    );
                    dispatch(
                        updateAttribute(
                            "flowExportPubmaticTemplateFields",
                            template.FlowExportTemplateId,
                            "IsAutoGeneratedPOM",
                            false
                        )
                    );
                }
            }

            const rows = [...templateRows];
            for (let row of rows) {
                row.ExpansionType = globalExpansionType;
                row.ExpirationDate = globalExpirationDate;

                if (DeliveryType.ThirdPartyCustom == deliveryType?.DeliveryType) {
                    row.PercentOfMediaCost = 0;
                    row.CostPerMillion = globalCostPerMillion;
                    row.IsAutoGeneratedCPM = false;
                    row.IsAutoGeneratedPOM = false;
                }
            }

            setTemplateRows(rows);
            setRefreshTable(refreshTable + 1);
            dispatch(syncScheduleEndDateWithExpiration(globalExpirationDate));
        }
    };

    const getColumns = useMemo(() => {
        const thisDeliveryType = tapadDeliveryTypes.find(x => x.TapadDeliveryTypeId == tapadDeliveryTypeId);
        let columnData = [
            {
                id: "AudienceName",
                header: "Audience Name",
                accessorFn: row => row.AudienceName,
                cell: info =>
                    selectAudienceName({
                        ...info.row.original,
                        tableData: { id: info.row.index },
                    }),
                size: 150,
            },
            {
                id: "Description",
                header: "Description",
                accessorFn: row => row.Description,
                cell: info =>
                    selectDescription({
                        ...info.row.original,
                        tableData: { id: info.row.index },
                    }),
                size: 150,
            },
            {
                id: "ExpansionType",
                header: "Expansion Type",
                accessorFn: row => row.ExpansionType,
                cell: info =>
                    selectExpansionType({
                        ...info.row.original,
                        tableData: { id: info.row.index },
                    }),
                size: 150,
            },
            {
                id: "ExpirationDate",
                header: "Expiration Date",
                accessorFn: row => row.ExpirationDate,
                cell: info =>
                    selectExpirationDate({
                        ...info.row.original,
                        tableData: { id: info.row.index },
                    }),
                size: 150,
            },
        ];

        if (DeliveryType.ThirdPartyCustom == thisDeliveryType?.DeliveryType) {
            columnData.push({
                id: "CostPerMillion",
                header: "CPM ($)",
                accessorFn: row => row.CostPerMillion,
                cell: info =>
                    selectCostPerMillion({
                        ...info.row.original,
                        tableData: { id: info.row.index },
                    }),
                meta: {
                    getCellStyle: row => getCellStyleCPM(null, row),
                },
                size: 150,
            });
        }

        return columnData;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshTable, deliveryType, canEdit, flow, tapadDeliveryTypeId, tapadDeliveryTypes]);

    const table = useReactTable({
        data: templateRows,
        columns: getColumns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const theme = createTheme({
        overrides: {
            MuiTable: {
                root: {
                    marginTop: "0px !important",
                },
            },
            MuiTableCell: {
                root: {
                    fontSize: "14px",
                },
            },
        },
    });

    return (
        <>
            <div className="flow-edit-header" style={{ flexWrap: "wrap" }}>
                <div style={{ display: "flex", flexGrow: 1 }}>
                    <p>Driver File Fields </p>
                </div>
                <div className="flow-edit-header-actions" style={{ display: "flex", marginLeft: "20px" }}>
                    <div style={{ display: "flex", marginLeft: "40px" }}> </div>
                </div>
            </div>
            <Divider style={{ margin: "20px auto" }} />
            <ThemeProvider theme={theme}>
                <ListItem
                    key="button-global-fields-assignment"
                    style={{ display: "flex", justifyContent: "center" }}
                    button
                    onClick={() => setIsOpenGlobalAssignment(!isOpenGlobalAssignment)}
                >
                    <span style={{ display: "flex", fontSize: "16px" }}> Global Fields Assignment </span>
                    <span style={{ display: "flex", position: "absolute", right: "0", fontSize: "16px" }}>
                        {isOpenGlobalAssignment ? ExpandLess : ExpandMore}
                    </span>
                </ListItem>
                <Collapse
                    key="container-global-fields-assignment"
                    in={isOpenGlobalAssignment}
                    timeout="auto"
                    unmountOnExit
                >
                    <div className="row" style={{ height: "100%" }}>
                        <div className="col col-sm-4 col-md-3">
                            <FormControl fullWidth style={{ margin: "10px 0 0" }}>
                                <InputLabel shrink style={{ fontSize: 14 }}>
                                    Expansion Type
                                </InputLabel>
                                <Select
                                    value={globalExpansionType}
                                    onChange={e => setGlobalExpansionType(e.target.value)}
                                    disabled={!canEdit || (flow && flow.IsLocked)}
                                    displayEmpty
                                >
                                    <MenuItem key={2} value={"HOUSEHOLD"}>
                                        Household
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            {DeliveryType.ThirdPartyCustom == deliveryType?.DeliveryType ? (
                                <NumericalTextField
                                    label="% of Media Spend"
                                    numberType="percent"
                                    style={{ fontSize: "14px", width: "100%", margin: "10px 0 0" }}
                                    value={globalPercentOfMediaCost + "%"}
                                    disabled={!canEdit || (flow && flow.IsLocked)}
                                    onBlur={e => handleGlobalPositiveValueChange("PercentOfMediaCost", e)}
                                />
                            ) : null}
                        </div>
                        <div className="col col-sm-4 col-md-3">
                            {DeliveryType.ThirdPartyCustom == deliveryType?.DeliveryType ? (
                                <NumericalTextField
                                    label="CPM"
                                    numberType="currency"
                                    style={{ fontSize: "14px", width: "100%", margin: "10px 0 0" }}
                                    value={USDollar.format(globalCostPerMillion)}
                                    disabled={!canEdit || (flow && flow.IsLocked)}
                                    onBlur={e => handleGlobalPositiveValueChange("CostPerMillion", e)}
                                />
                            ) : null}
                            <MuiPickersUtilsProvider utils={MomentUtils}>
                                <DateTimePicker
                                    label="Expiration Date"
                                    style={{ fontSize: "14px", width: "100%", margin: "10px 0 0" }}
                                    format={"MMM DD, YYYY HH:mm"}
                                    value={globalExpirationDate}
                                    onChange={e => handleGlobalDateTimeChange(e)}
                                />
                            </MuiPickersUtilsProvider>
                        </div>
                        <div className="col col-sm-4 col-md-6" style={{ height: "100%" }}>
                            <Button
                                color="primary"
                                variant="contained"
                                style={{ position: "absolute", bottom: "0" }}
                                onClick={assignGlobalValues}
                            >
                                Assign
                            </Button>
                        </div>
                    </div>
                </Collapse>
                <TableContainer style={{ maxHeight: 400, overflowX: "auto", margin: "20px 0px" }}>
                    <Table stickyHeader style={{ tableLayout: "fixed" }}>
                        <TableHead>
                            {table.getHeaderGroups().map(headerGroup => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map(header => (
                                        <TableCell
                                            key={header.id}
                                            style={{
                                                backgroundColor: "#0075bc",
                                                color: "#fff",
                                                fontWeight: 400,
                                                fontSize: "14px",
                                            }}
                                        >
                                            {header.isPlaceholder ? null : header.column.getCanSort() ? (
                                                <TableSortLabel
                                                    style={{ color: "#fff" }}
                                                    active={header.column.getIsSorted() !== false}
                                                    direction={header.column.getIsSorted() === "desc" ? "desc" : "asc"}
                                                    onClick={header.column.getToggleSortingHandler()}
                                                >
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                </TableSortLabel>
                                            ) : (
                                                flexRender(header.column.columnDef.header, header.getContext())
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHead>
                        <TableBody>
                            {table.getRowModel().rows.map(row => (
                                <TableRow key={row.id}>
                                    {row.getVisibleCells().map(cell => (
                                        <TableCell
                                            key={cell.id}
                                            style={{
                                                fontSize: "14px",
                                                padding: "2px",
                                                ...(cell.column.columnDef.meta?.getCellStyle
                                                    ? cell.column.columnDef.meta.getCellStyle(row.original)
                                                    : {}),
                                            }}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </ThemeProvider>
            <div id="templateFieldsTable" style={{ width: "100%", overflowX: "auto" }}>
                {" "}
            </div>
            <Divider style={{ margin: "40px auto 20px auto" }} />
        </>
    );
};

ExportPubmaticTemplateFields.propTypes = {
    tapadDeliveryTypeId: PropTypes.number,
    destinationId: PropTypes.number,
    flowExportPubmaticTemplateFields: PropTypes.array,
    canEdit: PropTypes.bool,
    classes: PropTypes.any,
};

export default withStyles(styles)(ExportPubmaticTemplateFields);
